import React, { useState, useEffect } from "react"
import { toast } from "sonner"
import axiosInstance from "../../utils/axios/axiosConfig"
import { UserPlus, Camera, Upload, Trash2, Lock } from "lucide-react"

const Settings = () => {
  const [loading, setLoading] = useState(false)
  const [profileData, setProfileData] = useState({
    fullName: "",
    email: "",
    phone: "",
    image: null,
    role: "",
    double_auth: false,
  })
  const [passwordData, setPasswordData] = useState({
    current: "",
    newPass: "",
    confirmNewPass: "",
  })

  useEffect(() => {
    fetchProfileData()
  }, [])

  const fetchProfileData = async () => {
    try {
      setLoading(true)
      const response = await axiosInstance.get("/users/profile")
      setProfileData(response.data.data)
      setLoading(false)
    } catch (error) {
      toast.error("Failed to fetch profile data")
      setLoading(false)
    }
  }

  const handleProfileChange = (e) => {
    const { name, value, type, checked } = e.target
    setProfileData((prevData) => ({
      ...prevData,
      [name]: type === "checkbox" ? checked : value,
    }))
  }

  const handlePasswordChange = (e) => {
    const { name, value } = e.target
    setPasswordData((prevData) => ({
      ...prevData,
      [name]: value,
    }))
  }

  const handleImageChange = (e) => {
    const file = e.target.files[0]
    if (file && file.size <= 15 * 1024 * 1024) {
      const reader = new FileReader()
      reader.onloadend = () => {
        setProfileData((prevData) => ({
          ...prevData,
          image: reader.result,
        }))
      }
      reader.readAsDataURL(file)
    } else {
      toast.error("Image size should be less than 15MB")
    }
  }

  const updateProfile = async (e) => {
    e.preventDefault()
    try {
      setLoading(true)
      const response = await axiosInstance.put("/users/updateProfile", {
        ...profileData,
        double_auth: profileData.double_auth,
      })
      toast.success(response.data.message)
      setLoading(false)
    } catch (error) {
      toast.error("Failed to update profile")
      setLoading(false)
    }
  }

  const changePassword = async (e) => {
    e.preventDefault()
    if (passwordData.newPass !== passwordData.confirmNewPass) {
      toast.error("New passwords do not match")
      return
    }
    try {
      setLoading(true)
      const response = await axiosInstance.put("/users/changePassword", passwordData)
      toast.success(response.data.message)
      setPasswordData({ current: "", newPass: "", confirmNewPass: "" })
      setLoading(false)
    } catch (error) {
      toast.error("Failed to change password")
      setLoading(false)
    }
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="flex flex-col items-start gap-1 mb-6">

        <h1 className="text-xl font-semibold text-neutral-900 dark:text-white">Profile Settings</h1>
        <p className="text-sm text-start text-neutral-600 ">Here you can change profile info and change password .</p>

      </div>
      <div className="space-y-8">
        {/* Profile Information */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-lg font-semibold text-neutral-800 mb-6 flex items-center">
            <UserPlus className="w-5 h-5 mr-2" />
            Profile Information
          </h2>
          <form onSubmit={updateProfile} className="space-y-6">
            <div className="flex items-center space-x-6">
              <div className="relative">
                <div className="w-24 h-24 rounded-full overflow-hidden bg-neutral-100">
                  {profileData.image ? (
                    <img
                      src={profileData.image || "/placeholder.svg"}
                      alt="Profile"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="flex items-center justify-center w-full h-full">
                      <Camera className="w-8 h-8 text-neutral-400" />
                    </div>
                  )}
                </div>
                <label
                  htmlFor="photo-upload"
                  className="absolute bottom-0 right-0 bg-white rounded-full p-1 shadow-md cursor-pointer hover:bg-neutral-50"
                >
                  <Upload className="w-4 h-4 text-neutral-600" />
                  <input
                    id="photo-upload"
                    type="file"
                    className="hidden"
                    onChange={handleImageChange}
                    accept="image/*"
                    name="image"
                  />
                </label>
              </div>
              <div>
                <p className="text-sm text-neutral-500">JPG, GIF or PNG. Max size of 15MB.</p>
                {profileData.image && (
                  <button
                    type="button"
                    onClick={() => setProfileData({ ...profileData, image: null })}
                    className="mt-2 text-sm text-red-600 hover:text-red-700 flex items-center"
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Remove photo
                  </button>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1 text-start" htmlFor="fullName">
                  Full Name
                </label>
                <input
                  type="text"
                  id="fullName"
                  name="fullName"
                  value={profileData.fullName}
                  onChange={handleProfileChange}
                  className="w-full px-3 py-2 bg-neutral-50 h-11 rounded-md focus:outline-none "
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1 text-start" htmlFor="email">
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={profileData.email}
                  onChange={handleProfileChange}
                  className="w-full px-3 py-2 bg-neutral-50 h-11 rounded-md focus:outline-none "
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1 text-start" htmlFor="phone">
                  Phone
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={profileData.phone}
                  onChange={handleProfileChange}
                  className="w-full px-3 py-2 bg-neutral-50 h-11 rounded-md focus:outline-none "
                />
              </div>
            </div>

            <div className="flex flex-col lg:flex-row items-center bg-neutral-50 p-4 rounded-md justify-between">
              <div>
                <h3 className="text-sm font-medium text-start text-neutral-900">Two-Factor Authentication (2FA)</h3>
                <p className="mt-1 text-start text-sm text-neutral-600">Add an extra layer of security to your account.</p>
              </div>
              <button
                type="button"
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${profileData.double_auth
                  ? "bg-red-100 text-red-800 hover:bg-red-200"
                  : "bg-green-100 text-green-800 hover:bg-green-200"
                  }`}
                onClick={() => {
                  setProfileData((prevData) => ({
                    ...prevData,
                    double_auth: !prevData.double_auth,
                  }))
                }}
              >
                {profileData.double_auth ? "Disable 2FA" : "Enable 2FA"}
              </button>
            </div>

            <div className="flex justify-end mt-3">
              <button
                type="submit"
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-[#2B2B35] rounded-lg hover:bg-cyan-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                disabled={loading}
              >
                <UserPlus className="w-4 h-4 mr-2" />
                {loading ? "Updating..." : "Update Profile"}
              </button>
            </div>
          </form>
        </div>

        {/* Password Change */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-lg font-semibold text-neutral-800 mb-6 flex items-center">
            <Lock className="w-5 h-5 mr-2" />
            Change Password
          </h2>
          <form onSubmit={changePassword} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1 text-start" htmlFor="current">
                Current Password
              </label>
              <input
                type="password"
                id="current"
                name="current"
                value={passwordData.current}
                onChange={handlePasswordChange}
                className="w-full px-3 py-2 bg-neutral-50 h-11 rounded-md focus:outline-none "
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1 text-start" htmlFor="newPass">
                New Password
              </label>
              <input
                type="password"
                id="newPass"
                name="newPass"
                value={passwordData.newPass}
                onChange={handlePasswordChange}
                className="w-full px-3 py-2 bg-neutral-50 h-11 rounded-md focus:outline-none "
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1 text-start" htmlFor="confirmNewPass">
                Confirm New Password
              </label>
              <input
                type="password"
                id="confirmNewPass"
                name="confirmNewPass"
                value={passwordData.confirmNewPass}
                onChange={handlePasswordChange}
                className="w-full px-3 py-2 bg-neutral-50 h-11 rounded-md focus:outline-none "
              />
            </div>
            <div className="flex justify-end mt-3">
              <button
                type="submit"
                className="inline-flex items-center  px-4 py-2 text-sm font-medium text-white bg-[#2B2B35] rounded-lg hover:bg-cyan-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                disabled={loading}
              >
                <Lock className="w-4 h-4 mr-2" />
                {loading ? "Changing..." : "Change Password"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default Settings

